/**
 * Keeps the hidden input[name="package_details"] honest.
 *
 * The pricing card and tabbed gallery CTAs write a package label into that
 * input and preselect a matching input[name="product"] radio. The label only
 * describes that one product, so it must not survive the visitor picking a
 * different one — otherwise the CRM lead carries a package that contradicts
 * its Product.
 *
 * Rather than track whether a change event came from a CTA or a human, the
 * form records which product the label belongs to. Any change to a different
 * product clears the label. That is ordering-independent, works across module
 * boundaries, and survives browser form restoration.
 */

export const PACKAGE_DETAILS_NAME = 'package_details';
export const PRODUCT_NAME = 'product';

// Rendered as data-cta-package-product / data-package-details-bound.
const CTA_PRODUCT_KEY = 'ctaPackageProduct';
const BOUND_KEY = 'packageDetailsBound';

export function getPackageDetailsInput(form) {
    return form ? form.querySelector('input[name="' + PACKAGE_DETAILS_NAME + '"]') : null;
}

export function clearPackageDetails(form) {
    const input = getPackageDetailsInput(form);

    if (input) {
        input.value = '';
    }

    delete form.dataset[CTA_PRODUCT_KEY];
}

/**
 * Record a CTA's package label against the product it describes.
 *
 * Must run before the caller ticks the radio, so the synthetic change event
 * sees a matching product and leaves the label alone.
 */
export function applyPackageDetails(form, packageValue, packageDetails) {
    const input = getPackageDetailsInput(form);

    if (!input) {
        return;
    }

    bindPackageDetailsReset(form);

    // A CTA without a label clears whatever an earlier CTA left behind.
    if (!packageDetails) {
        clearPackageDetails(form);

        return;
    }

    input.value = packageDetails;
    form.dataset[CTA_PRODUCT_KEY] = packageValue ?? '';
}

export function bindPackageDetailsReset(form) {
    if (!form || form.dataset[BOUND_KEY] === 'true') {
        return;
    }

    form.dataset[BOUND_KEY] = 'true';

    form.addEventListener('change', function (event) {
        const target = event.target;

        if (!target || target.name !== PRODUCT_NAME) {
            return;
        }

        if (target.value === form.dataset[CTA_PRODUCT_KEY]) {
            return;
        }

        clearPackageDetails(form);
    });
}

/**
 * Guard every form on the page that carries a package_details input.
 *
 * Runs on load, so the reset applies even when no CTA is clicked. Any value
 * present at this point cannot have come from a CTA in this page load — it is
 * browser form restoration after a reload or back-navigation — so it is
 * dropped rather than trusted.
 */
export function initPackageDetailsGuards(scope) {
    const root = scope || document;

    root.querySelectorAll('form').forEach(function (form) {
        if (!getPackageDetailsInput(form)) {
            return;
        }

        clearPackageDetails(form);
        bindPackageDetailsReset(form);
    });
}
